/**
 * 学员端内置 SVG 图标集
 * -----------------------------------------------------------------------------
 * 为什么手写而不是引图标库：
 *   - antd-mobile 5 **不带图标集**（TabBar.Item / Grid 的 icon 参数只是 ReactNode 槽位，
 *     传字符串会被当文字渲染——tab 显示「home」就是这个原因）；
 *   - 宫格入口的 `icon` 名（form-edit / wallet / truck…）是服务端 seed / 后台配置
 *     下发的约定枚举（见 `apps/server/prisma/seed.ts` 的 GRID_ENTRANCES），
 *   - 引第三方图标库要么体积大、要么运行时取网（学员端要弱网可用），不值。
 *
 * 风格：24×24 线性描边（stroke=currentColor），颜色随主题令牌走。
 * `EntranceIcon` 负责把服务端 icon 名映射到组件；**未知名字回落 `fallback`**，
 * 保证后台新配入口时前端不至于空白。
 */
import type { ReactNode } from 'react';

interface IconProps {
  size?: number;
  /** 描边粗细。默认 1.8；选中态 / 强调态可略粗（1.8 → 2.1）。 */
  strokeWidth?: number;
}

function Svg({ size = 24, strokeWidth = 1.8, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** 首页：房子 */
export function HomeIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9.5V20h13V9.5" />
      <path d="M9.5 20v-6h5v6" />
    </Svg>
  );
}

/** 消息：对话气泡 */
export function MessageIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 5.5h16v11H9l-5 4z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </Svg>
  );
}

/** 我的：人形 */
export function UserIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.2-3.4 3.8-5 7-5s5.8 1.6 7 5" />
    </Svg>
  );
}

/** 报名办理：表单 + 笔 */
export function FormEditIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M11 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V13" />
      <path d="M8 9h5M8 13h3" />
      <path d="m13.5 12.5 6-6 2 2-6 6-2.6.6z" />
    </Svg>
  );
}

/** 报名记录：单据列表 */
export function RecordsIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4.5" y="3.5" width="15" height="17" rx="1.5" />
      <path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" />
    </Svg>
  );
}

/** 缴费指引：钱包 */
export function WalletIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="6" width="17" height="13" rx="2" />
      <path d="M3.5 9.5h17" opacity="0" />
      <path d="M16 12.5h4.5V16H16a1.75 1.75 0 1 1 0-3.5z" />
      <path d="M6.5 6V4.8A1.3 1.3 0 0 1 7.8 3.5h9" />
    </Svg>
  );
}

/** 教材发放：货车 */
export function TruckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2.5 6.5h11v10h-11z" />
      <path d="M13.5 10h4l3 3v3.5h-7" />
      <circle cx="6.5" cy="18.5" r="1.8" />
      <circle cx="16.5" cy="18.5" r="1.8" />
    </Svg>
  );
}

/** 证书查询：放大镜 */
export function SearchIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="m15 15 5 5" />
    </Svg>
  );
}

/** 客服联系：耳麦 */
export function ServiceIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.5 13v-2a7.5 7.5 0 0 1 15 0v2" />
      <rect x="3" y="12.5" width="4" height="6" rx="1.6" />
      <rect x="17" y="12.5" width="4" height="6" rx="1.6" />
      <path d="M19 18.5v1a2.5 2.5 0 0 1-2.5 2.5H13" />
    </Svg>
  );
}

/** 帮助中心：圆 + 问号 */
export function HelpIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.8 9.5A2.4 2.4 0 0 1 14.4 10c0 1.6-2.4 1.9-2.4 3.5" />
      <path d="M12 16.8v.2" />
    </Svg>
  );
}

/** 返回箭头（导航栏左侧，设计稿 5.5 × 11 的细箭头） */
export function ChevronLeftIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M15 4.5 7.5 12l7.5 7.5" strokeWidth={2} />
    </Svg>
  );
}

/** 列表行右侧箭头 */
export function ChevronRightIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m9.5 5.5 6.5 6.5-6.5 6.5" strokeWidth={1.7} />
    </Svg>
  );
}

/** 勾选（成功态圆点内、时间轴节点） */
export function CheckIcon(props: IconProps) {
  return (
    <Svg {...props} strokeWidth={2.6}>
      <path d="m5 12.5 4.6 4.5L19 6.5" />
    </Svg>
  );
}

/** 铃铛（首页消息入口 / 空态） */
export function BellIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 9.5a6 6 0 0 1 12 0c0 3.2.7 5 1.6 6.2H4.4C5.3 14.5 6 12.7 6 9.5z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </Svg>
  );
}

/** 信息（蓝色提示条） */
export function InfoIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.2" />
      <path d="M12 7.9v.2" />
    </Svg>
  );
}

/** 警示（琥珀提示条） */
export function AlertIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.6v5.6" />
      <path d="M12 16.4v.2" />
    </Svg>
  );
}

/** 品牌标识（引导页顶部渐变方块内） */
export function ShieldIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.2 19 6v5.4c0 4.3-2.9 7.6-7 9.4-4.1-1.8-7-5.1-7-9.4V6z" />
      <path d="m8.8 12 2.2 2.2 4.2-4.4" />
    </Svg>
  );
}

/** 相机（证件照拍摄） */
export function CameraIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 8.8h3.2L8.6 6.5h6.8l1.4 2.3H20v10.2H4z" />
      <circle cx="12" cy="13.5" r="3.2" />
    </Svg>
  );
}

/** 人像占位（待拍摄方框） */
export function PortraitIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="9" r="3.2" />
      <path d="M6 19c1-3.2 3.2-4.8 6-4.8s5 1.6 6 4.8" />
    </Svg>
  );
}

/** 兜底：文档（后台新配的 icon 名前端未收录时显示，不空白） */
export function FallbackIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 3.5h8L19 8v12.5H6z" />
      <path d="M13.5 3.5V8H19" />
    </Svg>
  );
}

/** 教材包裹：立方体 */
export function BoxIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.2 20.2 7.6v8.8L12 20.8 3.8 16.4V7.6z" />
      <path d="M3.8 7.6 12 12l8.2-4.4" />
      <path d="M12 12v8.8" />
    </Svg>
  );
}

/** 证书：奖章（圆环 + 绶带） */
export function CertIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="9" r="5.2" />
      <path d="m8.6 13.4-1.4 6.4 4.8-2.6 4.8 2.6-1.4-6.4" />
    </Svg>
  );
}

/** 发票：票据（矩形 + 抬头行 + 金额行 + 锯齿底边） */
export function InvoiceIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 3.8h12v15.4l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3V3.8z" />
      <path d="M9 8.2h6M9 11.6h6" />
    </Svg>
  );
}

/** 服务端入口 icon 名 → 组件（与 seed 的 GRID_ENTRANCES 约定一致） */
const ENTRANCE_ICONS: Record<string, (props: IconProps) => ReactNode> = {
  'form-edit': FormEditIcon,
  records: RecordsIcon,
  wallet: WalletIcon,
  truck: TruckIcon,
  box: BoxIcon,
  search: SearchIcon,
  cert: CertIcon,
  invoice: InvoiceIcon,
  service: ServiceIcon,
  help: HelpIcon,
  user: UserIcon,
};

export function EntranceIcon({ name }: { name: string | null }) {
  const Component = (name && ENTRANCE_ICONS[name]) || FallbackIcon;
  return <Component size={22} />;
}
